import React, { useContext, useMemo, memo, useCallback } from "react";
import { FixedSizeList as List } from "react-window";
import { mainContext } from "@ts/constants";
import { addBrandsToList, addSellersToList } from "@ts/utils";
import {
    IMainContext,
    SETTINGS_TABLE_DATA_TYPE,
    TBrandsData,
    TSellersData,
} from "@ts/common-types";

interface ISellersComponent {
    isLoading: boolean;
    filteredSellersData: TSellersData[] | TBrandsData[];
    title?: SETTINGS_TABLE_DATA_TYPE;
}

// Memoized row component that renders two items per row to match the original grid
const SellerRow = memo(({
    index,
    style,
    data,
}: {
    index: number;
    style: React.CSSProperties;
    data: {
        filteredSellersData: TSellersData[] | TBrandsData[];
        isBrandsData: boolean;
        selectedSellersData: any;
        setSelectedSellersData: any;
        selectedBrandsData: any;
        setSelectedBrandsData: any;
        renderSellerCheckbox: (data: TSellersData | TBrandsData) => React.ReactNode;
    };
}) => {
    const {
        filteredSellersData,
        renderSellerCheckbox,
    } = data;

    // Render two items per row to match the original 2-column grid
    const leftItem = filteredSellersData[index * 2];
    const rightItem = filteredSellersData[index * 2 + 1];

    return (
        <div style={{ ...style, display: "flex", width: "100%" }}>
            <div style={{ width: "50%" }}>
                {leftItem && renderSellerCheckbox(leftItem)}
            </div>
            <div style={{ width: "50%" }}>
                {rightItem && renderSellerCheckbox(rightItem)}
            </div>
        </div>
    );
});

const SellersSelectionComponent: React.FC<ISellersComponent> = ({
    isLoading,
    filteredSellersData,
    title,
}) => {
    const { selectedSellersData, setSelectedSellersData, setSelectedBrandsData, selectedBrandsData } = useContext<IMainContext>(mainContext);

    const isBrandsData = useMemo(() => Boolean(title), [title]);

    const renderSellerCheckbox = useCallback((data: TSellersData | TBrandsData) => {
        const label = isBrandsData
            ? (data as TBrandsData).tag_name
            : (data as TSellersData).seller_name;
        const isLongName = label.length > 35;

        return (
            <div className="d-flex" key={data.id}>
                <label
                    title={label}
                    className="m-2"
                    style={{ display: isLongName ? "flex" : "inline" }}
                >
                    <input
                        type="checkbox"
                        className="sellers-checkbox"
                        name={label}
                        checked={
                            isBrandsData
                                ? (data as TBrandsData).is_selected
                                : (data as TSellersData).isPreferredSeller
                        }
                        value={label}
                        id={String(data.id)}
                        onChange={(e) => {
                            if (isBrandsData) {
                                addBrandsToList(
                                    e,
                                    selectedBrandsData,
                                    setSelectedBrandsData,
                                    filteredSellersData as TBrandsData[]
                                );
                            } else {
                                addSellersToList(
                                    e,
                                    selectedSellersData,
                                    setSelectedSellersData,
                                    filteredSellersData as TSellersData[]
                                );
                            }
                        }}
                    />
                    &nbsp;
                    <span
                        className="ellipsis"
                        style={{
                            width: "25ch",
                            marginLeft: isLongName ? "5px" : undefined,
                        }}
                    >
                        {label}
                    </span>
                </label>
            </div>
        );
    }, [isBrandsData, selectedBrandsData, setSelectedBrandsData,
         selectedSellersData, setSelectedSellersData, filteredSellersData]);

    const itemData = useMemo(() => ({
        filteredSellersData,
        isBrandsData,
        selectedSellersData,
        setSelectedSellersData,
        selectedBrandsData,
        setSelectedBrandsData,
        renderSellerCheckbox,
    }), [
        filteredSellersData,
        isBrandsData,
        selectedSellersData,
        setSelectedSellersData,
        selectedBrandsData,
        setSelectedBrandsData,
        renderSellerCheckbox,
    ]);

    if (isLoading) {
        return <i className="spinner-border" id="sellers-container-loader" />;
    }

    if (!filteredSellersData.length) {
        return <span id="sellers-no-data">{`No ${isBrandsData ? "Brands" : "Sellers"} Data Available`}</span>;
    }

    // Calculate rows needed for 2-column layout
    const rowCount = Math.ceil(filteredSellersData.length / 2);
    const estimatedItemHeight = 50;
    const maxVisibleRows = Math.min(6, rowCount); // Show max 6 rows at once (12 items)
    const listHeight = Math.min(280, maxVisibleRows * estimatedItemHeight);

    return (
        <div 
            className="sellers-container-inner d-block text-center overflow-hidden" 
            style={{ 
                width: "725px",
                maxHeight: "280px",
                fontSize: "1rem",
            }}
        >
            <List
                height={listHeight}
                itemCount={rowCount}
                itemSize={estimatedItemHeight}
                itemData={itemData}
                width="100%"
                overscanCount={2}
            >
                {SellerRow}
            </List>
        </div>
    );
};

export default SellersSelectionComponent;